import React from "react";
import { useTranslation } from "react-i18next";

interface PaymentSummaryProps {
  paidTotals: { rsd: number; eur: number };
  unpaidInTimeTotals: { rsd: number; eur: number };
  unpaidOverdueTotals: { rsd: number; eur: number };
  fixedCostsTotals?: { rsd: number; eur: number };
  onUnpaidInTimeClick?: () => void;
  onUnpaidOverdueClick?: () => void;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("sr-RS", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  paidTotals,
  unpaidInTimeTotals,
  unpaidOverdueTotals,
  fixedCostsTotals,
  onUnpaidInTimeClick,
  onUnpaidOverdueClick,
}) => {
  const { t } = useTranslation();

  const totalRsd = paidTotals.rsd + unpaidInTimeTotals.rsd + unpaidOverdueTotals.rsd;
  const paidPct = totalRsd > 0 ? Math.min(100, (paidTotals.rsd / totalRsd) * 100) : 0;
  const inTimePct = totalRsd > 0 ? Math.min(100, (unpaidInTimeTotals.rsd / totalRsd) * 100) : 0;
  const overduePct = totalRsd > 0 ? Math.min(100, (unpaidOverdueTotals.rsd / totalRsd) * 100) : 0;

  const hasFixed = fixedCostsTotals && (fixedCostsTotals.rsd > 0 || fixedCostsTotals.eur > 0);

  return (
    <div className="exp-grid">
      {/* ── Paid card ── */}
      <div className="exp-card paid">
        <div className="exp-card-head">
          <span className="exp-icon paid">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/>
            </svg>
          </span>
          <div>
            <div className="exp-label">
              {t("Paid expenses")} <span className="exp-tag">{t("from analytics")}</span>
            </div>
            <div className="exp-amount">{fmt(paidTotals.rsd)} <span className="cur">RSD</span></div>
            {paidTotals.eur > 0 && (
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                + {fmt(paidTotals.eur)} EUR
              </div>
            )}
          </div>
        </div>

        <div className="exp-card-body">
          {hasFixed && (
            <div className="exp-row">
              <span className="exp-row-ic" style={{ color: "#16a34a" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/>
                </svg>
              </span>
              <span className="exp-row-label">{t("Including fixed costs")}</span>
              <span className="exp-row-val">
                {fmt(fixedCostsTotals!.rsd)} <span className="cur">RSD</span>
              </span>
            </div>
          )}

          <div className="exp-bar">
            <div className="exp-bar-track">
              <div
                className="exp-bar-fill"
                style={{ width: `${paidPct}%`, background: "linear-gradient(90deg, #16a34a, #22c55e)" }}
              />
            </div>
            <div className="exp-bar-meta">
              <span>{t("Covered")}</span>
              <span>{paidPct.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Unpaid in-time card ── */}
      <div
        className="exp-card unpaid"
        onClick={onUnpaidInTimeClick}
        role={onUnpaidInTimeClick ? "button" : undefined}
      >
        <div className="exp-card-head">
          <span className="exp-icon unpaid">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>
            </svg>
          </span>
          <div>
            <div className="exp-label">{t("Unpaid within deadline")}</div>
            <div className="exp-amount warn">{fmt(unpaidInTimeTotals.rsd)} <span className="cur">RSD</span></div>
            {unpaidInTimeTotals.eur > 0 && (
              <div style={{ fontSize: 13, color: "#d97706", marginTop: 2 }}>
                + {fmt(unpaidInTimeTotals.eur)} EUR
              </div>
            )}
          </div>
        </div>

        <div className="exp-card-body">
          <div className="exp-bar">
            <div className="exp-bar-track">
              <div
                className="exp-bar-fill"
                style={{ width: `${inTimePct}%`, background: "linear-gradient(90deg, #d97706, #f59e0b)" }}
              />
            </div>
            <div className="exp-bar-meta">
              <span>{t("Outstanding")}</span>
              <span>{inTimePct.toFixed(1)}%</span>
            </div>
          </div>

          {onUnpaidInTimeClick && (
            <button className="exp-view-all">
              {t("Open all suppliers")}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M9 6l6 6-6 6"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Overdue card ── */}
      <div
        className="exp-card overdue"
        onClick={onUnpaidOverdueClick}
        role={onUnpaidOverdueClick ? "button" : undefined}
      >
        <div className="exp-card-head">
          <span className="exp-icon overdue">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/>
            </svg>
          </span>
          <div>
            <div className="exp-label">{t("Unpaid overdue")}</div>
            <div className="exp-amount overdue">{fmt(unpaidOverdueTotals.rsd)} <span className="cur">RSD</span></div>
            {unpaidOverdueTotals.eur > 0 && (
              <div style={{ fontSize: 13, color: "#dc2626", marginTop: 2 }}>
                + {fmt(unpaidOverdueTotals.eur)} EUR
              </div>
            )}
          </div>
        </div>

        <div className="exp-card-body">
          <div className="exp-bar">
            <div className="exp-bar-track">
              <div
                className="exp-bar-fill"
                style={{ width: `${overduePct}%`, background: "linear-gradient(90deg, #dc2626, #ef4444)" }}
              />
            </div>
            <div className="exp-bar-meta">
              <span>{t("Overdue")}</span>
              <span>{overduePct.toFixed(1)}%</span>
            </div>
          </div>

          {onUnpaidOverdueClick && (
            <button className="exp-view-all overdue">
              {t("Open all suppliers")}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M9 6l6 6-6 6"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
